import QuestionCircleOutlined from '@ant-design/icons/lib/icons/QuestionCircleOutlined';
import { App, Input, Switch, Tooltip } from 'antd';
import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import { useToggleDepartmentExpress } from '@/common/query/common.hooks';
import { useDepartments } from '@/common/useDepartments';
import { AppModal } from '@/components/ui/AppModal';
import { useAuth } from '@/core/providers/AuthFirebaseProvider';
import { Roles } from '@/core/utils/roles';
import { formatError, logError } from '@/utils/formatError';

const { TextArea } = Input;

export const DepartmentExpressToggle: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const { selectedDepartment, selectedDepartmentId } = useDepartments();
  const { userDataParams } = useAuth();
  const { mutateAsync: toggleExpress, isPending } = useToggleDepartmentExpress();

  const isManager = userDataParams?.roles.includes(Roles.manager) ?? false;
  const isExpressEnabled =
    (selectedDepartment as { is_express_enabled?: boolean })?.is_express_enabled ?? true;
  const disabledReason =
    (selectedDepartment as { disabled_express_reason?: string | null })?.disabled_express_reason ??
    null;

  const [modalVisible, setModalVisible] = useState(false);
  const [reason, setReason] = useState('');
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleEnable = useCallback(async () => {
    try {
      await toggleExpress({
        data: {
          departmentId: selectedDepartmentId,
          isExpressEnabled: true,
          disabledExpressReason: null,
        },
      });
      messageApi.success('DTK Express enabled for department');
      setPendingValue(null);
    } catch (error) {
      const errorMessage = formatError(error, 'Enable Department Express');
      logError(error, 'Enable Department Express');
      messageApi.error(`Failed to enable DTK Express: ${errorMessage}`);
    }
  }, [toggleExpress, selectedDepartmentId, messageApi]);

  const handleSwitchChange = useCallback(
    (checked: boolean) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        if (checked) {
          handleEnable();
        } else {
          setPendingValue(false);
          setModalVisible(true);
        }
      }, 300);
    },
    [handleEnable]
  );

  const handleDisable = useCallback(async () => {
    if (!reason.trim()) {
      messageApi.error('Please provide a reason for disabling DTK Express');
      return;
    }

    try {
      await toggleExpress({
        data: {
          departmentId: selectedDepartmentId,
          isExpressEnabled: false,
          disabledExpressReason: reason.trim(),
        },
      });
      messageApi.success('DTK Express disabled for department');
      setModalVisible(false);
      setReason('');
      setPendingValue(null);
    } catch (error) {
      const errorMessage = formatError(error, 'Disable Department Express');
      logError(error, 'Disable Department Express');
      messageApi.error(`Failed to disable DTK Express: ${errorMessage}`);
    }
  }, [toggleExpress, selectedDepartmentId, reason, messageApi]);

  const handleModalCancel = useCallback(() => {
    setModalVisible(false);
    setReason('');
    setPendingValue(null);
  }, []);

  const currentValue = pendingValue !== null ? pendingValue : isExpressEnabled;

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ fontWeight: 500 }}>DTK Express:</span>
        {!isExpressEnabled && disabledReason ? (
          <Tooltip title={`DTK Express is disabled. Reason: ${disabledReason}`}>
            <QuestionCircleOutlined style={{ color: '#faad14', cursor: 'help', flexShrink: 0 }} />
          </Tooltip>
        ) : null}
        <Switch
          className="app-switch"
          checked={currentValue}
          onChange={handleSwitchChange}
          disabled={isPending || !isManager}
          loading={isPending}
          style={{ flexShrink: 0 }}
        />
      </div>
      <AppModal
        title="Disable DTK Express"
        open={modalVisible}
        onOk={handleDisable}
        onCancel={handleModalCancel}
        okText="Disable"
        cancelText="Cancel"
        confirmLoading={isPending}
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: 16 }}>
          <p>Please provide a reason for disabling DTK Express in this department:</p>
        </div>
        <TextArea
          rows={4}
          placeholder="Enter reason for disabling DTK Express..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={isPending}
        />
      </AppModal>
    </>
  );
};
